import React, { useContext, useEffect } from "react";
import { Box, ButtonGroup, Button } from "@mui/material";
import { PivotViewConfig } from "../../entities/pivot_view_config";
import { DatePivotParams } from "../../entities/date_pivot_params";
import { SearchableDropdown } from "../../components/searchable_dropdown";
import {
  ParsedParquetData,
  isChunkedParsedData,
} from "../../utils/parquet";
import { SupplyWalkEditsContext } from "../../contexts/supply_walk_edits/provider";

export const SupplyWalkEditableCustomControls = <
  T extends DatePivotParams = DatePivotParams,
>({
  viewControllerConfig,
  params,
  setParams,
  isLoading,
  isChartView,
  setIsChartView,
  sortConfig,
  setSortConfig,
  dateHierarchyColumns,
  segmentColumns,
  measureColumns,
  filterColumns,
  columnSchema,
  parsedData,
}: {
  viewControllerConfig: PivotViewConfig<T>;
  params: T;
  setParams: (params: T) => void;
  isLoading: boolean;
  isChartView?: boolean;
  setIsChartView?: React.Dispatch<React.SetStateAction<boolean>>;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  dateHierarchyColumns: {
    name: string;
    caption: string;
    min: string;
    max: string;
  }[];
  segmentColumns: { name: string; caption: string }[];
  measureColumns: { name: string; caption: string }[];
  filterColumns: { name: string; caption: string; unique_values: string[] }[];
  columnSchema: Record<
    string,
    {
      dtype: string;
      min?: string;
      max?: string;
      unique_values?: string[];
    }
  >;
  parsedData?: ParsedParquetData | null;
}) => {
  const availableStatuses = ["Delivered", "Open", "Needs Review", "Approved"];

  // Get context to update approved row count
  const supplyWalkEdits = useContext(SupplyWalkEditsContext);

  // Extract status counts from parsed data (full table only; chunked path has no arrowTable)
  const getStatusCounts = () => {
    if (
      !parsedData ||
      isChunkedParsedData(parsedData) ||
      !parsedData.arrowTable
    ) {
      return {
        delivered_count: 0,
        open_count: 0,
        needs_review_count: 0,
        approved_count: 0,
      };
    }

    try {
      // Get the count values from the first row using proper Arrow table access
      // Convert to array and get first row
      const allRows = parsedData.arrowTable.toArray() as Record<string, unknown>[];

      // Extract counts from any row (they should be the same across all rows)
      // Check if there's a dummy row with count values (for when status filter results in 0 rows)
      let countRow =
        allRows.find(
          (row: Record<string, unknown>) =>
            row.purchase_order_id === "DUMMY"
        ) || allRows[0];

      // If no rows exist, set default counts
      if (!countRow) {
        countRow = {
          delivered_count: 0,
          open_count: 0,
          needs_review_count: 0,
          approved_count: 0,
        };
      }

      const delivered_count = Number(countRow?.delivered_count) || 0;
      const open_count = Number(countRow?.open_count) || 0;
      const needs_review_count = Number(countRow?.needs_review_count) || 0;
      const approved_count = Number(countRow?.approved_count) || 0;

      return {
        delivered_count,
        open_count,
        needs_review_count,
        approved_count,
      };
    } catch (error) {
      console.warn("Error extracting status counts:", error);
      return {
        delivered_count: 0,
        open_count: 0,
        needs_review_count: 0,
        approved_count: 0,
      };
    }
  };

  const statusCounts = getStatusCounts();

  // Update approved row count in context when data changes (initial load only)
  useEffect(() => {
    if (supplyWalkEdits?.setApprovedRowCount) {
      supplyWalkEdits.setApprovedRowCount(statusCounts.approved_count);
    }
  }, [statusCounts.approved_count, supplyWalkEdits]);

  // Get current status filter value
  const currentStatusFilter = params.filters.find(
    (filter) => filter[0] === "status"
  );
  const currentStatus = currentStatusFilter
    ? (currentStatusFilter[2] as string[])[0]
    : "";

  const handleStatusClick = (selectedStatus: string) => {
    // Remove existing status filter
    const updatedFilters = params.filters.filter(
      (filter) => filter[0] !== "status"
    );

    // Add new status filter if a status is selected
    if (selectedStatus) {
      updatedFilters.push(["status", "in", [selectedStatus]]);
    }

    setParams({
      ...params,
      filters: updatedFilters,
    });
  };

  // Get current SKU filter value
  const currentSkuFilter = params.filters.find((filter) => filter[0] === "sku");
  const currentSku = currentSkuFilter
    ? (currentSkuFilter[2] as string[])[0]
    : "";

  // Get SKU options from filterColumns
  const skuColumn = filterColumns.find((col) => col.name === "sku");
  const skuOptions = skuColumn?.unique_values || [];

  const handleSkuChange = (selectedSku: string) => {
    // Remove existing SKU filter
    const updatedFilters = params.filters.filter(
      (filter) => filter[0] !== "sku"
    );

    // Add new SKU filter if a SKU is selected
    if (selectedSku) {
      updatedFilters.push(["sku", "in", [selectedSku]]);
    }

    setParams({
      ...params,
      filters: updatedFilters,
    });
  };

  // Get current SKU name filter value
  const currentSkuNameFilter = params.filters.find(
    (filter) => filter[0] === "sku_name"
  );
  const currentSkuName = currentSkuNameFilter
    ? (currentSkuNameFilter[2] as string[])[0]
    : "";

  // Get SKU name options from filterColumns
  const skuNameColumn = filterColumns.find((col) => col.name === "sku_name");
  const skuNameOptions = skuNameColumn?.unique_values || [];

  const handleSkuNameChange = (selectedSkuName: string) => {
    // Remove existing SKU name filter
    const updatedFilters = params.filters.filter(
      (filter) => filter[0] !== "sku_name"
    );

    // Add new SKU name filter if a SKU name is selected
    if (selectedSkuName) {
      updatedFilters.push(["sku_name", "in", [selectedSkuName]]);
    }

    setParams({
      ...params,
      filters: updatedFilters,
    });
  };

  // Get current location filter value
  const currentLocationFilter = params.filters.find(
    (filter) => filter[0] === "location"
  );
  const currentLocation = currentLocationFilter
    ? (currentLocationFilter[2] as string[])[0]
    : "";

  // Get location options from filterColumns
  const locationColumn = filterColumns.find((col) => col.name === "location");
  const locationOptions = locationColumn?.unique_values || [];

  const handleLocationChange = (selectedLocation: string) => {
    // Remove existing location filter
    const updatedFilters = params.filters.filter(
      (filter) => filter[0] !== "location"
    );

    // Add new location filter if a location is selected
    if (selectedLocation) {
      updatedFilters.push(["location", "in", [selectedLocation]]);
    }

    setParams({
      ...params,
      filters: updatedFilters,
    });
  };

  // Get current supplier filter value
  const currentSupplierFilter = params.filters.find(
    (filter) => filter[0] === "supplier"
  );
  const currentSupplier = currentSupplierFilter
    ? (currentSupplierFilter[2] as string[])[0]
    : "";

  // Get supplier options from filterColumns
  const supplierColumn = filterColumns.find((col) => col.name === "supplier");
  const supplierOptions = supplierColumn?.unique_values || [];

  const handleSupplierChange = (selectedSupplier: string) => {
    // Remove existing supplier filter
    const updatedFilters = params.filters.filter(
      (filter) => filter[0] !== "supplier"
    );

    // Add new supplier filter if a supplier is selected
    if (selectedSupplier) {
      updatedFilters.push(["supplier", "in", [selectedSupplier]]);
    }

    setParams({
      ...params,
      filters: updatedFilters,
    });
  };

  // Get current PO ID filter value
  const currentPoIdFilter = params.filters.find(
    (filter) => filter[0] === "purchase_order_id"
  );
  const currentPoId = currentPoIdFilter
    ? (currentPoIdFilter[2] as string[])[0]
    : "";

  // Get PO ID options from filterColumns
  const poIdColumn = filterColumns.find(
    (col) => col.name === "purchase_order_id"
  );
  const poIdOptions = poIdColumn?.unique_values || [];

  const handlePoIdChange = (selectedPoId: string) => {
    // Remove existing PO ID filter
    const updatedFilters = params.filters.filter(
      (filter) => filter[0] !== "purchase_order_id"
    );

    // Add new PO ID filter if a PO ID is selected
    if (selectedPoId) {
      updatedFilters.push(["purchase_order_id", "in", [selectedPoId]]);
    }

    setParams({
      ...params,
      filters: updatedFilters,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        p: 1,
        pt: 0,
        flexWrap: "wrap",
        justifyContent: "space-between",
        width: "100%",
        minHeight: "55px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
        <ButtonGroup size="small" variant="outlined" sx={{ height: "32px" }}>
          {availableStatuses.map((status) => {
            const countKey =
              `${status.toLowerCase().replace(" ", "_")}_count` as keyof typeof statusCounts;
            const count: number = Number(statusCounts[countKey]) || 0;

            return (
              <Button
                key={status}
                onClick={() => handleStatusClick(status)}
                variant={currentStatus === status ? "contained" : "outlined"}
              >
                {status} ({count})
              </Button>
            );
          })}
        </ButtonGroup>
        {/* Export button removed; default header Download is now conditional */}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          borderRadius: 1,
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", gap: 1, mr: 1 }}>
          {" "}
          {/* <SearchOutlined fontSize="small" sx={{ mt: 0.5 }} /> */}
          filters:
        </Box>
        <SearchableDropdown
          label="po id"
          selectedValue={String(currentPoId || "")}
          handleChange={handlePoIdChange}
          options={poIdOptions}
          isLoading={isLoading}
        />
        <SearchableDropdown
          label="sku"
          selectedValue={String(currentSku || "")}
          handleChange={handleSkuChange}
          options={skuOptions}
          isLoading={isLoading}
        />
        <SearchableDropdown
          label="sku name"
          selectedValue={String(currentSkuName || "")}
          handleChange={handleSkuNameChange}
          options={skuNameOptions}
          isLoading={isLoading}
        />
        <SearchableDropdown
          label="location"
          selectedValue={String(currentLocation || "")}
          handleChange={handleLocationChange}
          options={locationOptions}
          isLoading={isLoading}
        />
        <SearchableDropdown
          label="supplier"
          selectedValue={String(currentSupplier || "")}
          handleChange={handleSupplierChange}
          options={supplierOptions}
          isLoading={isLoading}
        />
      </Box>
    </Box>
  );
};
